<template>
  <!--
    Aviso de cobranca da licenca do sistema (ver api/app/lib/ConfigPgLib.js).

    Montado no Layout.vue, e nao no App.vue, DE PROPOSITO: o Layout envolve
    exclusivamente as telas autenticadas do sistema, entao este componente nao
    existe em /login nem em /app (o PWA do cliente final). O requisito "nunca na
    /app" fica garantido pela arvore de componentes, e nao por um `if` que
    alguem pode quebrar depois.

    O fetch fica no watcher de $route, e nao no router: o `beforeEnter` do
    record pai do Layout so dispara ao ENTRAR no Layout — navegar de /pedido
    para /caixa nao o executa (em vue-router 3 o record pai cai em `updated`,
    nao em `activated`). Um PDV aberto desde as 10h nunca reavaliaria.
  -->
  <div v-if="temAviso">
    <b-modal
      v-model="modalAberto"
      :title="titulo"
      centered
      hide-footer
      :no-close-on-backdrop="bloqueado"
      :no-close-on-esc="bloqueado"
      header-bg-variant="transparent"
      modal-class="modal-aviso-pagamento"
    >
      <div class="corpo">
        <div class="faixa" :class="classeEstado">
          <i :class="icone" />
          <span>{{ resumo }}</span>
        </div>

        <p class="mensagem">{{ mensagem }}</p>

        <div v-if="pix" class="pix">
          <vue-qrcode
            :value="pix"
            :color="{ dark: '#000000ff', light: '#ffffffff' }"
            type="image/png"
            :width="190"
            :margin="2"
            :errorCorrectionLevel="'M'"
          />
          <div class="copia">
            <code>{{ pixResumido }}</code>
            <b-button size="sm" variant="outline-dark" @click="copiarPix">
              <i class="far fa-copy" /> Copiar código Pix
            </b-button>
          </div>
        </div>

        <div class="acoes">
          <b-button variant="secondary" size="sm" @click="fechar">
            {{ bloqueado ? "Continuar mesmo assim" : "Fechar" }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!--
      Barra fina no rodape (e nao no topo): o `.conteudo` do layout e
      position:absolute com top:0, entao uma barra no topo cobriria o cabecalho
      de todas as telas. Aqui basta um padding-bottom no proprio .conteudo.
    -->
    <div
      v-if="mostrarBarra"
      class="barra-pagamento"
      :class="classeEstado"
      role="button"
      tabindex="0"
      @click="abrir"
      @keyup.enter="abrir"
    >
      <i class="fas fa-exclamation-triangle" />
      <span>{{ textoBarra }}</span>
      <small>toque para ver</small>
    </div>
  </div>
</template>

<script>
import VueQrcode from "vue-qrcode";
import {
  estadoPagamento,
  atualizarPagamento,
} from "@/utils/travaPagamento";

// Reavaliacao periodica para quem deixa a mesma tela aberta o dia inteiro
// (PDV, salao). A navegacao ja cobre o uso normal.
const INTERVALO_MS = 15 * 60 * 1000;

export default {
  name: "AvisoPagamento",
  components: { VueQrcode },

  data() {
    return {
      pg: estadoPagamento,
      fechado: false,
      timer: null,
    };
  },

  computed: {
    temAviso() {
      return this.pg.estado !== "OK";
    },
    bloqueado() {
      return this.pg.estado === "BLOQUEADO";
    },
    vencido() {
      return this.pg.estado === "VENCIDO" || this.bloqueado;
    },
    modalAberto: {
      get() {
        return this.temAviso && !this.fechado;
      },
      set(valor) {
        if (!valor) this.fechado = true;
      },
    },
    // So vira barra quando ja venceu. No lembrete, fechar e fechar — volta na
    // proxima navegacao.
    mostrarBarra() {
      return this.vencido && this.fechado;
    },
    classeEstado() {
      if (this.bloqueado) return "bloqueado";
      return this.vencido ? "vencido" : "lembrete";
    },
    icone() {
      return this.vencido
        ? "fas fa-exclamation-triangle"
        : "far fa-calendar-alt";
    },
    titulo() {
      return this.pg.titulo || "Mensalidade do sistema";
    },
    mensagem() {
      return this.pg.mensagem || "";
    },
    pix() {
      return this.pg.pix_copia_cola || "";
    },
    pixResumido() {
      if (this.pix.length <= 64) return this.pix;
      return this.pix.slice(0, 32) + "..." + this.pix.slice(-24);
    },
    resumo() {
      if (this.bloqueado) return "Lançamentos bloqueados por falta de pagamento";
      if (this.vencido) {
        const d = this.pg.dias_atraso;
        if (d === 0) return "Vence hoje";
        return "Vencido há " + d + (d === 1 ? " dia" : " dias");
      }
      const faltam = Math.max(0, -this.pg.dias_atraso);
      return "Vence em " + faltam + (faltam === 1 ? " dia" : " dias");
    },
    textoBarra() {
      if (this.bloqueado) return "Lançamentos bloqueados — mensalidade em aberto";
      const d = this.pg.dias_atraso;
      if (d === 0) return "Mensalidade do sistema vence hoje";
      return "Mensalidade do sistema vencida há " + d + (d === 1 ? " dia" : " dias");
    },
  },

  watch: {
    // Dispara em TODA navegacao: reabre o aviso e revalida (respeitando o TTL
    // de travaPagamento, entao clicar rapido entre telas nao vira uma rajada
    // de requisicoes).
    $route() {
      this.fechado = false;
      atualizarPagamento();
    },
    // A barra do rodape precisa reservar espaco no conteudo, que e
    // position:absolute e nao empurra sozinho.
    mostrarBarra: {
      immediate: true,
      handler(mostra) {
        if (typeof document === "undefined") return;
        document.body.classList.toggle("tem-barra-pagamento", !!mostra);
      },
    },
  },

  created() {
    atualizarPagamento();
    this.timer = setInterval(() => atualizarPagamento(true), INTERVALO_MS);
  },

  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (typeof document !== "undefined") {
      document.body.classList.remove("tem-barra-pagamento");
    }
  },

  methods: {
    fechar() {
      this.fechado = true;
    },
    abrir() {
      this.fechado = false;
    },
    async copiarPix() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.pix);
        } else {
          // Fallback para http (o dev roda em https, mas deploys antigos nao).
          const area = document.createElement("textarea");
          area.value = this.pix;
          area.style.position = "fixed";
          area.style.opacity = "0";
          document.body.appendChild(area);
          area.select();
          document.execCommand("copy");
          document.body.removeChild(area);
        }
        this.showToast("success", "Código Pix copiado!");
      } catch (e) {
        this.showToast("error", "Não foi possível copiar. Selecione o código manualmente.");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.corpo {
  text-align: left;
}

.faixa {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;

  &.lembrete {
    background: #fff8e1;
    color: #8a6100;
  }
  &.vencido {
    background: #fdecec;
    color: #a4161a;
  }
  &.bloqueado {
    background: #a4161a;
    color: #fff;
  }
}

.mensagem {
  white-space: pre-wrap;
  word-break: break-word;
  margin-bottom: 14px;
}

.pix {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px;
  background: #f6f6f7;
  border-radius: 10px;
  margin-bottom: 14px;
}

.copia {
  flex: 1;
  min-width: 200px;

  code {
    display: block;
    font-size: 11px;
    color: #444;
    word-break: break-all;
    margin-bottom: 8px;
  }
}

.acoes {
  display: flex;
  justify-content: flex-end;
}

.barra-pagamento {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  // Acima do conteudo, abaixo do modal-backdrop (1040) e do drawer do menu (1090).
  z-index: 1030;
  background: #c1121f;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.25);

  &.bloqueado {
    background: #6a040f;
  }

  small {
    opacity: 0.75;
    font-weight: 400;
  }
}
</style>

<style lang="scss">
/* Global (sem scoped) de proposito: o .conteudo pertence ao Layout e e
   position:absolute com bottom:0, entao nao cede espaco sozinho para a barra. */
body.tem-barra-pagamento .conteudo {
  padding-bottom: 26px;
}
</style>
